// Import Swiper and styles from npm package
import Swiper from 'swiper/bundle';
import PhotoSwipeLightbox from 'photoswipe/lightbox';
import 'swiper/css/bundle';
import 'swiper/css/thumbs';
import 'photoswipe/style.css';
// ============================================================================
// Base Configuration
// ============================================================================

const thumbsEl = document.querySelector('.thumbs-swiper');
const hasThumbs = thumbsEl?.dataset.hasThumbs === '1';
const baseConfig = {
    loadPrevNextAmount: 1,
    pagination: {
        el: '.swiper-pagination',
        clickable: true
    },
    navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev'
    }
};

// Deep merge function to properly merge nested objects
function deepMerge(...objects) {
    const isObject = (obj) => obj && typeof obj === 'object';

    return objects.reduce((prev, obj) => {
        Object.keys(obj).forEach((key) => {
            const pVal = prev[key];
            const oVal = obj[key];

            if (Array.isArray(pVal) && Array.isArray(oVal)) {
                prev[key] = pVal.concat(...oVal);
            } else if (isObject(pVal) && isObject(oVal)) {
                prev[key] = deepMerge(pVal, oVal);
            } else {
                prev[key] = oVal;
            }
        });

        return prev;
    }, {});
}

// Initialize Swiper with merged settings
function initSwiper(
    element,
    specificConfig,
    customSettings = {},
    blockSettings = {}
) {
    const mergedConfig = deepMerge(
        {},
        baseConfig,
        specificConfig,
        customSettings,
        blockSettings
    );
    return new Swiper(element, mergedConfig);
}

// ============================================================================
// Carousel Initializations
// ============================================================================

// Carousel Main - Product detail images with fullscreen
document.querySelectorAll('.carousel-main').forEach((element) => {
    const customSettings = element.dataset.swiper
        ? JSON.parse(element.dataset.swiper)
        : {};

    const specificConfig = {
        loadPrevNextAmount: 2,
        autoHeight: true
    };

    initSwiper(element, specificConfig, customSettings);
});

// Carousel Nav - Thumbnail navigation for main carousel
document.querySelectorAll('.carousel-nav').forEach((element) => {
    const customSettings = element.dataset.swiper
        ? JSON.parse(element.dataset.swiper)
        : {};

    const specificConfig = {
        loadPrevNextAmount: 10,
        slidesPerView: 'auto',
        watchSlidesProgress: true,
        freeMode: true,
        spaceBetween: 10,
        navigation: false,
        pagination: false
    };

    // Link to main carousel if exists
    const mainCarousel = document.querySelector('.carousel-main');
    if (mainCarousel && mainCarousel.swiper) {
        specificConfig.thumbs = {
            swiper: mainCarousel.swiper
        };
    }

    initSwiper(element, specificConfig, customSettings);
});

// Carousel Catalog - Auto-playing product carousel
document.querySelectorAll('.carousel-catalog').forEach((element) => {
    const customSettings = element.dataset.swiper
        ? JSON.parse(element.dataset.swiper)
        : {};

    const specificConfig = {
        loop: true,
        autoplay: {
            delay: 3000,
            disableOnInteraction: false
        }
    };
    initSwiper(element, specificConfig, customSettings);
});

// Thumbs Swiper - Thumbnail navigation for product gallery
let thumbsSwiper = null;

if (hasThumbs) {
    const customSettings = thumbsEl.dataset.swiper
        ? JSON.parse(thumbsEl.dataset.swiper)
        : {};

    thumbsSwiper = new Swiper(thumbsEl, {
        slidesPerView: 4,
        watchSlidesProgress: true,
        loop: true,
        spaceBetween: 15,
        mousewheel: {
            invert: true
        },
        centeredSlides: true,
        slideToClickedSlide: true,
        allowTouchMove: false,
        ...customSettings
    });
}
// Carousel Product Gallery - Product gallery with thumbnails and photoswipe
document.querySelectorAll('.carousel-product-gallery').forEach((element) => {
    const customSettings = element.dataset.swiper
        ? JSON.parse(element.dataset.swiper)
        : {};

    const specificConfig = {
        loadPrevNextAmount: 2,
        loop: true,
        ...(thumbsSwiper && {
            thumbs: {
                swiper: thumbsSwiper
            }
        }),
        on: {
            slideChange: function () {
                if (thumbsSwiper) {
                    thumbsSwiper.slideToLoop(this.realIndex);
                }
            }
        }
    };
    initSwiper(element, specificConfig, customSettings);
});

// PhotoSwipe initialization for product gallery
const pswpGallery = document.getElementById('pswp-gallery');
if (pswpGallery) {
    const lightbox = new PhotoSwipeLightbox({
        gallery: '#pswp-gallery',
        children: '.pswp-gallery-item',
        pswpModule: () => import('photoswipe'),
        showHideAnimationType: 'fade',
        padding: { top: 20, bottom: 20, left: 20, right: 20 }
    });
    // Sincronizzazione in tempo reale con la navigazione di PhotoSwipe
    lightbox.on('change', () => {
        const activeIndex = lightbox.pswp.currIndex;
        const mainSwiper = document.querySelector(
            '.carousel-product-gallery'
        ).swiper;

        if (mainSwiper) {
            mainSwiper.slideToLoop(activeIndex, 0, false);
        }
    });
    lightbox.init();
}

// Carousel Content - Content blocks carousel (loaded dynamically)
document.addEventListener('flickity-content-loaded', function (e) {
    // Carousel Content
    document.querySelectorAll('.carousel-content').forEach((element) => {
        const customSettings = element.dataset.swiper
            ? JSON.parse(element.dataset.swiper)
            : {};
        const blockSettings = element.dataset.swiperAdditionalBlockSettings
            ? JSON.parse(element.dataset.swiperAdditionalBlockSettings)
            : {};

        const specificConfig = {
            loop: true,
            autoplay: {
                delay: 6000,
                disableOnInteraction: false,
                pauseOnMouseEnter: true
            }
        };

        initSwiper(element, specificConfig, customSettings, blockSettings);
    });

    document
        .querySelectorAll('.brand-slider .carousel-content')
        .forEach((element) => {
            const customSettings = element.dataset.swiper
                ? JSON.parse(element.dataset.swiper)
                : {};
            const blockSettings = element.dataset.swiperAdditionalBlockSettings
                ? JSON.parse(element.dataset.swiperAdditionalBlockSettings)
                : {};

            const specificConfig = {
                loop: true,
                autoplay: {
                    delay: 6000,
                    disableOnInteraction: false,
                    pauseOnMouseEnter: true
                },
                slidesPerView: 2,
                spaceBetween: 10,
                keyboard: true,
                freeMode: true,
                breakpoints: {
                    992: {
                        slidesPerView: 3
                    },
                    1200: {
                        slidesPerView: 4
                    }
                }
            };

            initSwiper(element, specificConfig, customSettings, blockSettings);
        });

    // Carousel Catalog List - Category list slider
    document.querySelectorAll('.carousel-catalog-list').forEach((element) => {
        const customSettings = element.dataset.swiper
            ? JSON.parse(element.dataset.swiper)
            : {};
        const blockSettings = element.dataset.swiperAdditionalBlockSettings
            ? JSON.parse(element.dataset.swiperAdditionalBlockSettings)
            : {};

        const specificConfig = {
            autoHeight: true,
            slidesPerView: 2,
            spaceBetween: 10,
            slidesPerGroup: 2,
            keyboard: true,
            freeMode: true,
            breakpoints: {
                992: {
                    slidesPerView: 3,
                    slidesPerGroup: 3
                },
                1200: {
                    slidesPerView: 4,
                    slidesPerGroup: 4
                }
            }
        };

        initSwiper(element, specificConfig, customSettings, blockSettings);
    });

    // Carousel Product List - Product list slider
    document.querySelectorAll('.carousel-product-list').forEach((element) => {
        const customSettings = element.dataset.swiper
            ? JSON.parse(element.dataset.swiper)
            : {};
        const blockSettings = element.dataset.swiperAdditionalBlockSettings
            ? JSON.parse(element.dataset.swiperAdditionalBlockSettings)
            : {};

        const specificConfig = {
            autoHeight: true,
            slidesPerView: 2,
            spaceBetween: 5,
            slidesPerGroup: 2,
            keyboard: true,
            freeMode: true,
            breakpoints: {
                992: {
                    slidesPerView: 3,
                    slidesPerGroup: 3
                },
                1200: {
                    slidesPerView: 4,
                    slidesPerGroup: 4
                }
            }
        };

        initSwiper(element, specificConfig, customSettings, blockSettings);
    });
});

// ============================================================================
// Event Handlers
// ============================================================================

// Go to image by color code - Navigate to specific slide by color
/* $(document).on('click', '.go-to-image-color', function () {
    const codeColors = $(this).attr('data-color-code');

    if (codeColors && codeColors.length) {
        const mainCarousel = document.querySelector(
            '.carousel-product-gallery'
        );
        if (mainCarousel && mainCarousel.swiper) {
            const swiper = mainCarousel.swiper;
            let index = 0;
            let found = false;

            // Split color codes by comma and trim spaces
            const colorList = codeColors.split(',').map((c) => c.trim());

            $(mainCarousel)
                .find('.swiper-slide')
                .each(function (i) {
                    const dataCode = $(this).attr('data-code');

                    // Check if any color matches
                    for (let color of colorList) {
                        if (dataCode && dataCode.indexOf(color) >= 0) {
                            index = i;
                            found = true;
                            return false; // break out of each
                        }
                    }
                });

            if (found) {
                swiper.autoplay.stop();
                swiper.slideTo(index);
            }
        }
    }
});
 */
$(document).on('click', '.go-to-image-color', function () {
    const codeColors = $(this).attr('data-color-code');

    if (codeColors && codeColors.length) {
        const mainCarousel = document.querySelector(
            '.carousel-product-gallery'
        );

        if (mainCarousel && mainCarousel.swiper) {
            const swiper = mainCarousel.swiper;
            let targetIndex = -1;

            // Trasformiamo la stringa "BSW201SR,BSW201R" in un array pulito
            const colorList = codeColors
                .split(',')
                .map((c) => c.trim())
                .filter((c) => c.length);
            const slides = $(mainCarousel).find('.swiper-slide');

            // Exact match first; otherwise accept the color code followed by a size suffix
            // (e.g. S11970AG3XL for S11970AG). A plain prefix is not enough: truncated color codes
            // would hit other colors. Mirrors Product::matchGalleryCode().
            const findSlide = (isMatch) => {
                let found = -1;
                slides.each(function (i) {
                    const slideDataCode = $(this).attr('data-code');
                    if (
                        slideDataCode &&
                        colorList.some((c) => isMatch(slideDataCode, c))
                    ) {
                        found = i;
                        return false;
                    }
                });
                return found;
            };

            targetIndex = findSlide((slideCode, c) => slideCode === c);
            if (targetIndex === -1) {
                targetIndex = findSlide(
                    (slideCode, c) =>
                        slideCode.startsWith(c) &&
                        /^[2-9]?X[SL]$/i.test(slideCode.slice(c.length))
                );
            }

            // Se abbiamo trovato una slide corrispondente
            if (targetIndex !== -1) {
                swiper.autoplay.stop();
                // Usiamo l'indice reale della slide
                swiper.slideTo(targetIndex);
            }
        }
    }
});
